import { useState, useEffect } from 'react';
import {
  fetchCampaign,
  prepareCampaign,
  launchCampaign,
  cancelCampaign,
  retryFailedCampaign,
  fetchRecipients,
  cloneCampaign,
  exportCampaignCsv,
  updateCampaign,
} from '../../api/relances.api';
import RecipientTable from './RecipientTable';

const STATUS_BADGES = {
  DRAFT:     { bg: 'bg-slate-100',   text: 'text-slate-700',   label: 'Brouillon' },
  SCHEDULED: { bg: 'bg-sky-100',     text: 'text-sky-800',     label: 'Planifiée' },
  RUNNING:   { bg: 'bg-violet-100',  text: 'text-violet-800',  label: 'En cours' },
  DONE:      { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Terminée' },
  CANCELLED: { bg: 'bg-red-100',     text: 'text-red-800',     label: 'Annulée' },
};

const CHANNEL_MODES = {
  EMAIL_ONLY:    'Email uniquement',
  WHATSAPP_ONLY: 'WhatsApp uniquement',
  AUTO_FALLBACK: 'Email puis WhatsApp',
};

const CONTENT_MODES = {
  TEMPLATE:        'Template',
  TEMPLATE_EDITED: 'Template modifié',
  CUSTOM:          'Personnalisé',
};

function mapUiStatus(c) {
  if (c.status === 'DRAFT')                                return 'DRAFT';
  if (c.status === 'READY') return c.scheduled_at ? 'SCHEDULED' : 'DRAFT';
  if (c.status === 'SENDING')                              return 'RUNNING';
  if (['COMPLETED', 'FAILED'].includes(c.status))         return 'DONE';
  if (c.status === 'CANCELLED')                            return 'CANCELLED';
  return 'DRAFT';
}

export default function CampaignDetail({ campaignId, onBack }) {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRecipients, setShowRecipients] = useState(true);

  // R3 — dedup stats après prepare
  const [prepareStats, setPrepareStats] = useState(null);

  // R4 — preview du premier destinataire
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Clone / CSV / Reschedule
  const [exporting, setExporting] = useState(false);
  const [rescheduleAt, setRescheduleAt] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  useEffect(() => {
    loadCampaign();
  }, [campaignId]);

  const loadCampaign = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCampaign(campaignId);
      setCampaign(data);
    } catch (err) {
      setError('Erreur lors du chargement de la campagne');
    } finally {
      setLoading(false);
    }
  };

  const handlePrepare = async () => {
    if (!confirm('Préparer la campagne ? Les destinataires seront calculés.')) return;
    setActionLoading(true);
    setPrepareStats(null);
    try {
      const result = await prepareCampaign(campaignId);
      // Le backend retourne campaign + _prepare_stats
      const { _prepare_stats, ...campaignData } = result;
      setCampaign(campaignData);
      if (_prepare_stats) setPrepareStats(_prepare_stats);
    } catch (err) {
      alert(err.message || 'Erreur lors de la préparation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLaunch = async () => {
    const confirmMsg = campaign.is_dry_run
      ? `Lancer en mode TEST ? (${campaign.total_recipients} destinataires marqués sans envoi réel)`
      : `Lancer la campagne ? ${campaign.total_recipients} messages seront envoyés.`;
    if (!confirm(confirmMsg)) return;
    setActionLoading(true);
    try {
      const updated = await launchCampaign(campaignId, { confirmRecipientCount: campaign.total_recipients });
      setCampaign(updated);
    } catch (err) {
      alert(err.message || 'Erreur lors du lancement');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Annuler cette campagne ?')) return;
    setActionLoading(true);
    try {
      const updated = await cancelCampaign(campaignId);
      setCampaign(updated);
    } catch (err) {
      alert(err.message || "Erreur lors de l'annulation");
    } finally {
      setActionLoading(false);
    }
  };

  const handleClone = async () => {
    try {
      await cloneCampaign(campaignId);
      alert('Campagne dupliquée avec succès.');
    } catch (err) {
      alert(err?.message || 'Erreur lors de la duplication');
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      await exportCampaignCsv(campaignId, campaign?.name);
    } catch {
      alert('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleAt && !confirm('Supprimer la planification et lancer immédiatement ?')) return;
    setRescheduling(true);
    try {
      const updated = await updateCampaign(campaignId, {
        scheduledAt: rescheduleAt ? new Date(rescheduleAt).toISOString() : null
      });
      setCampaign(updated);
      setRescheduleAt('');
    } catch (err) {
      alert(err?.message || 'Erreur lors du reprogrammation');
    } finally {
      setRescheduling(false);
    }
  };

  // R5 — Retry failed recipients
  const handleRetryFailed = async () => {
    if (!confirm(`Relancer les ${campaign.failed_count} échecs ?`)) return;
    setActionLoading(true);
    try {
      const result = await retryFailedCampaign(campaignId);
      setCampaign(result.campaign);
      alert(`${result.retried} destinataire(s) remis en file d'attente.`);
    } catch (err) {
      alert(err.message || 'Erreur lors du retry');
    } finally {
      setActionLoading(false);
    }
  };

  // R4 — Charger preview du 1er destinataire rendu
  const handleShowPreview = async () => {
    if (previewData) { setPreviewOpen(v => !v); return; }
    setPreviewLoading(true);
    try {
      const data = await fetchRecipients(campaignId, { limit: 1, page: 1 });
      const first = (data.recipients || data)[0] || null;
      setPreviewData(first);
      setPreviewOpen(true);
    } catch {
      alert('Impossible de charger la prévisualisation.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Chargement...</div>;

  if (error || !campaign) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error || 'Campagne introuvable'}</p>
        <button onClick={onBack} className="text-slate-700 hover:underline">Retour à la liste</button>
      </div>
    );
  }

  const statusBadge = STATUS_BADGES[mapUiStatus(campaign)] || STATUS_BADGES.DRAFT;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700 mb-2 flex items-center gap-1">
            ← Retour à la liste
          </button>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3 flex-wrap">
            {campaign.name}
            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
              {statusBadge.label}
            </span>
            {campaign.is_dry_run && (
              <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                TEST
              </span>
            )}
            {campaign.sequence_delay_days && (
              <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
                Suivi J+{campaign.sequence_delay_days}
              </span>
            )}
          </h2>
          {campaign.scheduled_at && (
            <div className="text-xs text-slate-500 mt-1">📅 Planifiée : {formatDate(campaign.scheduled_at)}</div>
          )}
          {campaign.sequence_parent_id && (
            <div className="text-xs text-slate-500 mt-1">
              ↩ Suivi de{' '}
              <button
                onClick={() => {}}
                className="underline hover:text-slate-800"
                title={campaign.sequence_parent_id}
              >
                campagne parente
              </button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {campaign.status !== 'SENDING' && (
            <button onClick={handleClone}
              className="px-3 py-2 border border-slate-200 bg-white rounded-lg text-slate-700 hover:bg-slate-50 text-sm font-semibold"
              title="Dupliquer cette campagne">
              ⧉ Dupliquer
            </button>
          )}
          {campaign.status !== 'DRAFT' && (
            <button onClick={handleExportCsv} disabled={exporting}
              className="px-3 py-2 border border-slate-200 bg-white rounded-lg text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-semibold">
              {exporting ? '...' : '⬇ Export .csv'}
            </button>
          )}
          <a href="/agenda" className="px-3 py-2 border border-slate-200 bg-white rounded-lg text-slate-700 hover:bg-slate-50 text-sm font-semibold">
            📅 Agenda
          </a>
          {campaign.status === 'DRAFT' && (
            <button onClick={handlePrepare} disabled={actionLoading}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950 disabled:opacity-50 flex items-center gap-2 text-sm font-semibold">
              {actionLoading ? '...' : '🔄 Préparer'}
            </button>
          )}
          {campaign.status === 'READY' && (
            <button onClick={handleLaunch} disabled={actionLoading}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2 text-sm font-semibold">
              {actionLoading ? '...' : '🚀 Lancer'}
            </button>
          )}
          {/* R5 — Retry failed */}
          {campaign.failed_count > 0 && ['COMPLETED', 'FAILED', 'SENDING'].includes(campaign.status) && (
            <button onClick={handleRetryFailed} disabled={actionLoading}
              className="px-4 py-2 border border-orange-200 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 disabled:opacity-50 text-sm font-semibold">
              {actionLoading ? '...' : `↩ Relancer ${campaign.failed_count} échec(s)`}
            </button>
          )}
          {['DRAFT', 'READY', 'SENDING'].includes(campaign.status) && (
            <button onClick={handleCancel} disabled={actionLoading}
              className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 text-sm font-semibold">
              Annuler
            </button>
          )}
        </div>
      </div>

      {/* R3 — Banner déduplication après prepare */}
      {prepareStats && (
        <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800 flex items-center gap-3">
          <span>✅</span>
          <span>
            <strong>{prepareStats.candidates}</strong> candidat(s) trouvé(s)
            {prepareStats.duplicates > 0 && (
              <> → <strong>{prepareStats.inserted}</strong> destinataires uniques
              {' '}(<strong>{prepareStats.duplicates}</strong> doublon(s) fusionné(s))</>
            )}
            {prepareStats.duplicates === 0 && <> → <strong>{prepareStats.inserted}</strong> destinataires uniques</>}
          </span>
          <button onClick={() => setPrepareStats(null)} className="ml-auto text-sky-500 hover:text-sky-700 text-xs">✕</button>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Destinataires</div>
          <div className="text-2xl font-bold text-slate-900">{campaign.total_recipients || 0}</div>
          <div className="flex gap-4 mt-2 text-xs text-slate-500">
            <span>📧 {campaign.email_count || 0}</span>
            <span>📱 {campaign.whatsapp_count || 0}</span>
            <span className="text-orange-500">⚠ {campaign.unreachable_count || 0}</span>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Envoyés</div>
          <div className="text-2xl font-bold text-emerald-600">{campaign.sent_count || 0}</div>
          {campaign.failed_count > 0 && (
            <div className="text-xs text-red-500 mt-2">{campaign.failed_count} échec(s)</div>
          )}
          {campaign.skipped_count > 0 && (
            <div className="text-xs text-slate-500 mt-1">{campaign.skipped_count} ignoré(s)</div>
          )}
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Canal</div>
          <div className="text-lg font-medium text-slate-900">{CHANNEL_MODES[campaign.channel_mode] || campaign.channel_mode}</div>
          <div className="text-xs text-slate-500 mt-2">{CONTENT_MODES[campaign.content_mode] || campaign.content_mode}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Dates</div>
          <div className="text-xs space-y-1">
            <div><span className="text-slate-500">Créée :</span> {formatDate(campaign.created_at)}</div>
            {campaign.launched_at && <div><span className="text-slate-500">Lancée :</span> {formatDate(campaign.launched_at)}</div>}
            {campaign.completed_at && <div><span className="text-slate-500">Terminée :</span> {formatDate(campaign.completed_at)}</div>}
          </div>
        </div>
      </div>

      {/* Reschedule — READY only */}
      {campaign.status === 'READY' && (
        <div className="mb-6 bg-sky-50 border border-sky-200 rounded-lg p-4">
          <div className="text-sm font-medium text-sky-800 mb-2">📅 Modifier l'horaire d'envoi</div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="datetime-local"
              value={rescheduleAt}
              onChange={e => setRescheduleAt(e.target.value)}
              className="px-3 py-1.5 border border-sky-300 rounded-lg bg-white text-sm"
            />
            <button
              onClick={handleReschedule}
              disabled={rescheduling}
              className="px-3 py-1.5 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 text-sm font-semibold"
            >
              {rescheduling ? '...' : rescheduleAt ? 'Reprogrammer' : 'Envoyer maintenant'}
            </button>
          </div>
        </div>
      )}

      {/* Critères de ciblage */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
        <h3 className="font-medium text-slate-900 mb-3">Critères de ciblage</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          {campaign.targeting_criteria?.payments?.enabled && (
            <div className="bg-slate-50 rounded p-3">
              <div className="font-medium text-slate-700 mb-1">💰 Paiements</div>
              <div className="text-slate-500">
                {campaign.targeting_criteria.payments.minDaysOverdue && `Min ${campaign.targeting_criteria.payments.minDaysOverdue}j`}
                {campaign.targeting_criteria.payments.maxDaysOverdue && ` - Max ${campaign.targeting_criteria.payments.maxDaysOverdue}j`}
              </div>
            </div>
          )}
          {campaign.targeting_criteria?.prestations?.enabled && (
            <div className="bg-slate-50 rounded p-3">
              <div className="font-medium text-slate-700 mb-1">🔧 Prestations</div>
              <div className="text-slate-500">
                {campaign.targeting_criteria.prestations.minDaysOverdue && `Min ${campaign.targeting_criteria.prestations.minDaysOverdue}j`}
                {campaign.targeting_criteria.prestations.maxDaysOverdue && ` - Max ${campaign.targeting_criteria.prestations.maxDaysOverdue}j`}
              </div>
            </div>
          )}
          {campaign.targeting_criteria?.orders?.enabled && (
            <div className="bg-slate-50 rounded p-3">
              <div className="font-medium text-slate-700 mb-1">📦 Commandes</div>
              <div className="text-slate-500">
                {campaign.targeting_criteria.orders.minDaysOverdue && `Min ${campaign.targeting_criteria.orders.minDaysOverdue}j`}
              </div>
            </div>
          )}
          {campaign.targeting_criteria?.customers && (
            <div className="bg-slate-50 rounded p-3">
              <div className="font-medium text-slate-700 mb-1">👤 Clients</div>
              <div className="text-slate-500">
                Cooldown : {campaign.targeting_criteria.customers.cooldownDays || 7}j
                {campaign.targeting_criteria.customers.excludeOptOut && ' / Excl. opt-out'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* R4 — Preview email avant lancement */}
      {['READY', 'SENDING', 'COMPLETED', 'FAILED'].includes(campaign.status) && (
        <div className="mb-6">
          <button
            onClick={handleShowPreview}
            disabled={previewLoading}
            className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
          >
            <span>{previewOpen ? '▼' : '▶'}</span>
            {previewLoading ? 'Chargement...' : 'Prévisualisation du message'}
          </button>

          {previewOpen && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 space-y-3">
              {!previewData ? (
                <p className="text-sm text-slate-500">Aucun destinataire préparé.</p>
              ) : (
                <>
                  <div className="text-xs text-slate-400 font-semibold uppercase tracking-wide">
                    Exemple — {previewData.customer_name || previewData.customer_email || 'destinataire'}
                  </div>
                  {previewData.rendered_subject && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 mb-1">Sujet</div>
                      <div className="text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                        {previewData.rendered_subject}
                      </div>
                    </div>
                  )}
                  {previewData.rendered_body && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 mb-1">Corps de l'email</div>
                      <div
                        className="text-sm bg-slate-50 rounded-lg px-3 py-2 border border-slate-200 max-h-48 overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: previewData.rendered_body }}
                      />
                    </div>
                  )}
                  {previewData.rendered_whatsapp && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 mb-1">Message WhatsApp</div>
                      <div className="text-sm text-slate-900 bg-green-50 rounded-lg px-3 py-2 border border-green-200 whitespace-pre-line">
                        {previewData.rendered_whatsapp}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Destinataires */}
      {['READY', 'SENDING', 'COMPLETED', 'FAILED'].includes(campaign.status) && (
        <div>
          <button
            onClick={() => setShowRecipients(!showRecipients)}
            className="flex items-center gap-2 text-slate-700 font-medium mb-4"
          >
            <span>{showRecipients ? '▼' : '▶'}</span>
            Destinataires ({campaign.total_recipients})
          </button>
          {showRecipients && (
            <RecipientTable campaignId={campaignId} campaignStatus={campaign.status} />
          )}
        </div>
      )}
    </div>
  );
}
